import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '../../components/ui/popover';
import {
  listJobDescriptions,
  type ApiJobDescriptionSummary,
} from '../../libs/api/jobDescriptionService';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

interface JobTitleComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
  onSelectExisting: (jd: ApiJobDescriptionSummary) => void;
  maxLength?: number;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export default function JobTitleCombobox({
  value,
  onValueChange,
  onSelectExisting,
  maxLength,
  placeholder,
  className,
  disabled,
}: JobTitleComboboxProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState(value.trim());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(value.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value]);

  const { data, isFetching } = useQuery({
    queryKey: ['job-title-suggestions', debouncedQuery],
    queryFn: () => listJobDescriptions({ search: debouncedQuery, page_size: 8 }),
    enabled: debouncedQuery.length >= MIN_QUERY_LENGTH,
    staleTime: 30_000,
  });

  const suggestions = (data?.items ?? []).filter(
    (item) => item.title.toLowerCase() !== value.trim().toLowerCase(),
  );

  const open =
    isFocused &&
    !dismissed &&
    debouncedQuery.length >= MIN_QUERY_LENGTH &&
    (isFetching || suggestions.length > 0);

  return (
    <Popover open={open}>
      <PopoverAnchor asChild>
        <Input
          value={value}
          onChange={(event) => {
            setDismissed(false);
            onValueChange(event.target.value);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setDismissed(true);
            }
          }}
          maxLength={maxLength}
          className={className}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        className="w-[--radix-popover-trigger-width] overflow-hidden p-1"
      >
        {isFetching ? (
          <div className="flex items-center gap-2 px-2 py-2 text-sm text-text-subtle">
            <Loader2 className="size-3.5 animate-spin" /> Searching existing job descriptions...
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto">
            <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-text-subtle">
              Existing job descriptions
            </div>
            {suggestions.map((item) => (
              <button
                key={item.id}
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  setDismissed(true);
                  onSelectExisting(item);
                }}
                className="flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left hover:bg-primary-light/60"
              >
                <span className="truncate text-sm font-medium text-text-main">{item.title}</span>
                <span className="truncate text-xs text-text-subtle">
                  {[item.department, item.location].filter(Boolean).join(' · ') || 'No department'}
                </span>
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
